import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service, Slack } from 'webapp-types';
import { DeliveryType, ScheduleType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import FormCheckboxGroupComponent from 'webapp/components/form/checkbox-group';
import type { GroupSelected } from 'webapp/components/form/checkbox-group';
import GoalNotificationsRecipientsEmailsSelectorComponent from 'webapp/components/goal/notifications/recipients-emails-selector';
import NotificationsDeliveryTargetsComponent from 'webapp/components/notifications/delivery-targets';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import SubmitButtonComponent from 'webapp/components/submit-button';

import Goal from 'webapp/models-custom/goal';

export default class GoalNotificationsPopupComponent extends PopupInnerContextComponent<PopupData.Goal.Notifications> {
	@service('analytics') declare analyticsService: Service.Analytics;

	scheduleTypeOptions = [
		{ label: 'Daily', value: ScheduleType.DAILY },
		{ label: 'Weekly', value: ScheduleType.WEEKLY },
		{ label: 'Monthly', value: ScheduleType.MONTHLY },
		{ label: 'Quarterly', value: ScheduleType.QUARTERLY },
		{
			label: 'On status change',
			value: ScheduleType.ONE_OFF,
			tooltip:
				'Get notified if the goal is hit, missed, or its status changes (e.g., from on track to at risk) even before the period ends.',
			tooltipMaxWidth: 300,
		},
	];

	get model() {
		return this.popupData.model;
	}

	get modelAsGoal() {
		return this.model as Goal;
	}

	get showScheduleTypes() {
		// not defined on logs -- so will only show for goals
		return !!this.modelAsGoal.scheduleTypes;
	}

	get ownerId() {
		// for excludeUserId
		if (this.model instanceof Goal) {
			return this.modelAsGoal.ownerId;
		} else {
			return this.model.goal?.ownerId;
		}
	}

	get allowedDeliveryTypes() {
		return [DeliveryType.EMAIL, DeliveryType.SLACK];
	}

	get selectedDelivery() {
		return this.model.delivery;
	}

	get hasNoneScheduleTypesOrDeliveryTargets() {
		return this.modelAsGoal.scheduleTypes?.length === 0 || this.model.delivery.length === 0;
	}

	get submitButtonTooltipText() {
		return this.hasNoneScheduleTypesOrDeliveryTargets
			? 'Please select at least one frequency type and one delivery option'
			: undefined;
	}

	@action
	updateScheduleTypes(value: GroupSelected) {
		// only called for goals
		this.modelAsGoal.scheduleTypes = value as ScheduleType[];
	}

	@action
	updateDeliveryTargets(delivery: DeliveryType[]) {
		this.model.delivery = delivery;
	}

	@action
	updateSlackChannels(value: Slack.Channels): void {
		this.analyticsService.event(TrackingEvent.SLACK_CHANNELS_SELECTED, {
			origin: 'goal-notifications',
		});
		this.model.slackChannels = value as Slack.Channel[];
	}

	@action
	save(): void {
		this.popupData.onSave();
		this.close();
	}
	<template>
		{{#if this.showScheduleTypes}}
			<div class="row frequency">
				<label>Frequency</label>
				<FormCheckboxGroupComponent
					@name="frequency"
					@selected={{this.modelAsGoal.scheduleTypes}}
					@options={{this.scheduleTypeOptions}}
					@onChange={{this.updateScheduleTypes}}
				/>
			</div>
		{{/if}}

		<NotificationsDeliveryTargetsComponent
			@deliveryTypes={{this.allowedDeliveryTypes}}
			@selected={{this.selectedDelivery}}
			@onSelectionChange={{this.updateDeliveryTargets}}
			@slackChannels={{this.model.slackChannels}}
			@onSlackChannelsChange={{this.updateSlackChannels}}
		/>

		<hr />

		<div class="row">
			<label>Recipients</label>
			<GoalNotificationsRecipientsEmailsSelectorComponent
				@model={{this.model}}
				@excludeUserId={{this.ownerId}}
				@submitInProgress={{false}}
				@onContentChange={{this.scheduleReposition}}
			/>
		</div>

		<div class="controls2">
			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent
					@text="Apply"
					@onTrigger={{this.save}}
					@tooltipText={{this.submitButtonTooltipText}}
					@isDisabled={{this.hasNoneScheduleTypesOrDeliveryTargets}}
				/>
			</div>
		</div>
	</template>
}
